// Auto-Mix: Spurrollen, objektive Mix-Messung mit Referenz, lautstärkegleicher A/B-Vergleich und Blindvergleich.
// Messung und Referenz bleiben auf diesem Rechner (nichts wird hochgeladen). Messwerte helfen beim Vergleichen
// zwischen Fassungen desselben Songs; entscheiden tut das Hören.
import { useEffect, useRef, useState } from 'react';
import type { Project, Track } from '@srs/shared';
import { engine } from '../audio/engine.ts';
import { songEnd } from '../audio/render.ts';
import { toast } from '../components/ui.tsx';
import { useStudio } from '../state/store.ts';
import { togglePlay } from '../components/Transport.tsx';
import { BANDS, integratedLoudness, loudestWindow, measureMix, type MixReport, type Role } from '../automix/measure.ts';
import { ROLE_LABEL, roleFor, type Analysis } from '../automix/automix.ts';

const chans = (b: AudioBuffer) => Array.from({ length: b.numberOfChannels }, (_, c) => b.getChannelData(c));
const n1 = (x: number, unit = '') => (Number.isFinite(x) ? `${(Math.round(x * 10) / 10).toFixed(1).replace('.', ',')}${unit ? ` ${unit}` : ''}` : '–');
const mmss = (t: number) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;

/** Ganzen Song (höchstens 10 min) rendern und messen */
export async function measureProject(p: Project): Promise<MixReport> {
  const b = await engine.bounce(p, Math.min(songEnd(p), 600));
  return measureMix(chans(b), b.sampleRate);
}

/** Pegelunterschied zwischen zwei Mix-Ständen am Analysefenster (LU): so viel muss B lauter werden, um wie A zu klingen */
export async function loudnessGap(a: Project, b: Project, win: { from: number; len: number }): Promise<number> {
  const [x, y] = await Promise.all([engine.bounce(a, win.len, win.from), engine.bounce(b, win.len, win.from)]);
  const la = integratedLoudness(chans(x), x.sampleRate), lb = integratedLoudness(chans(y), y.sampleRate);
  return Number.isFinite(la) && Number.isFinite(lb) ? Math.max(-24, Math.min(24, la - lb)) : 0;
}
/** Vergleichsstand mit Lautheitsausgleich über den Master */
export const matched = (base: Project, gap: number): Project => ({ ...base, masterDb: base.masterDb + gap });

// ---------------------------------------------------------------------------
/** Spurrollen: aus dem Namen, aus dem Klang geschätzt oder vom Menschen gesetzt */
export function RoleTable({ project, an, roles, onRole }: { project: Project; an: Analysis; roles: Record<string, Role>; onRole: (id: string, r: Role | null) => void }) {
  const tracks = project.tracks.filter((t) => t.kind !== 'bus' && an.tracks[t.id]);
  const src = { name: 'aus dem Namen', klang: 'aus dem Klang geschätzt', mensch: 'von euch gesetzt' } as const;
  return (
    <div style={{ padding: '0 16px 14px' }}>
      <div className="row" style={{ justifyContent: 'space-between', margin: '4px 0 8px' }}>
        <b style={{ fontSize: 13 }}>Spurrollen</b>
        <span className="t2" style={{ fontSize: 12 }}>Analysiert: {mmss(an.win.from)} bis {mmss(an.win.from + an.win.len)} ({an.win.why})</span>
      </div>
      <table className="amtable">
        <thead><tr><th>Spur</th><th>Rolle</th><th>Herkunft</th><th>Lautheit</th><th>Bassanteil</th><th>Tonal</th></tr></thead>
        <tbody>
          {tracks.map((t: Track) => {
            const r = roleFor(t, an, roles), f = an.tracks[t.id];
            return (
              <tr key={t.id} className={r.src === 'klang' || r.role === 'other' ? 'low' : ''}>
                <td style={{ fontWeight: 600 }}>{t.name}</td>
                <td>
                  <select value={r.role} onChange={(e) => onRole(t.id, e.target.value as Role)}>
                    {(Object.keys(ROLE_LABEL) as Role[]).map((k) => <option key={k} value={k}>{ROLE_LABEL[k]}</option>)}
                  </select>
                </td>
                <td className="t2">{src[r.src]}{r.src === 'mensch' && <> <button className="btn small" title="Automatisch bestimmen" onClick={() => onRole(t.id, null)}>↺</button></>}</td>
                <td className="mono">{n1(f.lufs, 'LUFS')}</td>
                <td className="mono">{Math.round(f.lowRatio * 100)} %</td>
                <td className="mono">{Math.round(f.voiced * 100)} %</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="t2" style={{ fontSize: 12, marginTop: 8 }}>Die Rolle bestimmt Balance, EQ und Dynamik. Ändert ihr eine Rolle, werden die offenen Vorschläge neu berechnet; Entschiedenes bleibt.</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
type Col = { key: string; label: string; r?: MixReport };

/** Objektive Messung: Ist-Mix vor dem Auto-Mix, jetzt, Referenz */
export function MixReportPanel({ an, now, setNow, refr, setRef }: { an: Analysis; now: MixReport | null; setNow: (r: MixReport) => void; refr: { name: string; r: MixReport; buf: AudioBuffer } | null; setRef: (x: { name: string; r: MixReport; buf: AudioBuffer } | null) => void }) {
  const project = useStudio((s) => s.project);
  const [busy, setBusy] = useState('');
  const file = useRef<HTMLInputElement>(null);
  const refPlay = useRef<{ src: AudioBufferSourceNode } | null>(null);
  const [refOn, setRefOn] = useState(false);
  useEffect(() => () => { refPlay.current?.src.stop(); }, []);

  const cols: Col[] = [{ key: 'v', label: 'Vorher', r: an.mix }, { key: 'n', label: 'Jetzt', r: now ?? undefined }, { key: 'ref', label: refr ? `Referenz` : 'Referenz', r: refr?.r }];
  const rows: { label: string; get: (r: MixReport) => number; unit: string; warn?: (v: number) => string | undefined; hint: string }[] = [
    { label: 'Integrated', get: (r) => r.lufs, unit: 'LUFS', hint: 'Lautheit über den ganzen Song (EBU R128). Streaming-Dienste gleichen meist auf etwa −14 LUFS an.' },
    { label: 'Loudness Range', get: (r) => r.lra, unit: 'LU', hint: 'Wie stark die Lautheit zwischen leisen und lauten Teilen schwankt.' },
    { label: 'True Peak', get: (r) => r.truePeak, unit: 'dBTP', warn: (v) => (v > -1 ? 'red' : undefined), hint: 'Spitze inklusive Zwischenwerten. Über −1 dBTP kann es nach der Wandlung in MP3/AAC verzerren.' },
    { label: 'PLR', get: (r) => r.plr, unit: 'dB', hint: 'Abstand Spitze zu Lautheit: klein = stark verdichtet, groß = viel Dynamik.' },
    { label: 'Stereo-Korrelation', get: (r) => r.corr, unit: '', warn: (v) => (v < 0 ? 'red' : v < 0.2 ? 'amber' : undefined), hint: '+1 = mono, 0 = sehr breit, negativ = Phasenprobleme.' },
    { label: 'Mono-Verlust', get: (r) => r.monoLoss, unit: 'dB', warn: (v) => (v < -3 ? 'amber' : undefined), hint: 'So viel Pegel geht in der Mono-Summe verloren (Handy, Club-Anlage).' },
  ];

  async function loadRef(f: File) {
    setBusy('Referenz wird gemessen …');
    try {
      const ctx = await engine.ensure();
      const buf = await ctx.decodeAudioData(await f.arrayBuffer());
      setRef({ name: f.name.replace(/\.[^.]+$/, ''), r: measureMix(chans(buf), buf.sampleRate), buf });
      toast('Referenz gemessen. Sie bleibt auf diesem Rechner.');
    } catch { toast('Die Datei ließ sich nicht lesen. Bitte WAV, AIFF, MP3 oder AAC verwenden.', 6000); }
    finally { setBusy(''); }
  }

  async function playRef() {
    if (refPlay.current) { refPlay.current.src.stop(); refPlay.current = null; setRefOn(false); return; }
    if (!refr) return;
    if (useStudio.getState().transport.playing) await togglePlay();
    const ctx = await engine.ensure();
    const target = (now ?? an.mix).lufs, gap = Number.isFinite(target) && Number.isFinite(refr.r.lufs) ? target - refr.r.lufs : 0;
    const src = ctx.createBufferSource(); src.buffer = refr.buf; src.loop = true;
    const from = loudestWindow(chans(refr.buf), refr.buf.sampleRate, 15);
    src.loopStart = from; src.loopEnd = Math.min(refr.buf.duration, from + 15);
    const g = ctx.createGain(); g.gain.value = Math.pow(10, gap / 20);
    src.connect(g).connect(ctx.destination);
    src.start(0, from);
    refPlay.current = { src }; setRefOn(true);
    toast(`Referenz an ihrer lautesten Stelle, auf die Lautheit eures Mixes angeglichen (${gap > 0 ? '+' : ''}${n1(gap, 'dB')})`, 5000);
  }

  return (
    <div className="stack" style={{ padding: '0 16px 14px' }}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button className="btn small" disabled={!!busy} onClick={async () => { setBusy('Mix wird gemessen …'); try { setNow(await measureProject(project)); } finally { setBusy(''); } }}>Jetzt messen</button>
        <button className="btn small" disabled={!!busy} onClick={() => file.current?.click()}>{refr ? 'Andere Referenz laden' : 'Referenz laden'}</button>
        {refr && <button className={`btn small ${refOn ? 'on' : ''}`} onClick={() => void playRef()}>{refOn ? 'Referenz stoppen' : 'Referenz hören, lautstärkegleich'}</button>}
        {refr && <button className="btn small" onClick={() => { refPlay.current?.src.stop(); refPlay.current = null; setRefOn(false); setRef(null); }}>Referenz entfernen</button>}
        {busy && <span className="t2">{busy}</span>}
        <input ref={file} type="file" accept="audio/*,.wav,.aif,.aiff,.mp3,.m4a,.flac" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void loadRef(f); }} />
      </div>
      <table className="amtable mixcheck">
        <thead><tr><th>Messung</th>{cols.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} title={row.hint}>
              <td>{row.label}</td>
              {cols.map((c) => { const v = c.r ? row.get(c.r) : NaN; const w = c.r && row.warn?.(v); return <td key={c.key} className="mono">{c.r ? <span className={w ? `pill ${w}` : ''}>{row.unit ? n1(v, row.unit) : (Math.round(v * 100) / 100).toFixed(2).replace('.', ',')}</span> : <span className="muted">–</span>}</td>; })}
            </tr>
          ))}
          <tr><td colSpan={4} style={{ paddingTop: 14 }}><b style={{ fontSize: 12 }}>Klangbalance</b> <span className="t2" style={{ fontSize: 11.5 }}>Anteil je Band am Gesamtspektrum</span></td></tr>
          {BANDS.map((b) => (
            <tr key={b.id}>
              <td>{b.label} <span className="t2" style={{ fontSize: 11 }}>{b.lo < 1000 ? b.lo : `${b.lo / 1000}k`}–{b.hi < 1000 ? b.hi : `${b.hi / 1000}k`} Hz</span></td>
              {cols.map((c) => {
                if (!c.r) return <td key={c.key} className="muted">–</td>;
                const v = c.r.bands[b.id], base = (now ?? an.mix).bands[b.id], d = c.key === 'ref' ? v - base : 0;
                return <td key={c.key} className="mono"><span className="bandbar"><i style={{ width: `${Math.max(2, Math.min(100, (v + 30) * 3.3))}%` }} /></span>{n1(v, 'dB')}{c.key === 'ref' && Math.abs(d) >= 1.5 && <span className={`pill ${Math.abs(d) >= 3 ? 'amber' : ''}`} style={{ marginLeft: 6 }}>{d > 0 ? '+' : ''}{n1(d)}</span>}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="t2" style={{ fontSize: 12 }}>
        Die Referenz bleibt auf diesem Rechner und wird nicht hochgeladen. Messwerte vergleichen Fassungen, sie sind keine Qualitätsnote: ein Mix kann gut messen und trotzdem nicht tragen. Entscheidend ist der lautstärkegleiche Hörvergleich.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
export const CATS = [
  { id: 'balance', label: 'Balance', d: 'Lautstärkeverhältnisse der Spuren' },
  { id: 'clarity', label: 'Klarheit', d: 'Jede Spur hörbar, nichts verdeckt' },
  { id: 'pan', label: 'Panorama', d: 'Verteilung im Stereobild' },
  { id: 'space', label: 'Raum', d: 'Tiefe, Hall, vorne und hinten' },
  { id: 'dyn', label: 'Dynamik', d: 'Kontrolliert, aber lebendig' },
] as const;
export type Ratings = Record<(typeof CATS)[number]['id'], number>;

/**
 * Blindvergleich: Fassung mit Vorschlägen und Ausgangsstand in zufälliger Reihenfolge als X und Y, lautstärkegleich,
 * Bewertung 1–5 in fünf Kategorien, danach Auflösung. So entscheidet das Ohr und nicht das Wissen, welche Fassung die KI gemacht hat.
 */
export function BlindTest({ baseline, win, onDone }: { baseline: Project; win: { from: number; len: number }; onDone: (line: string) => void }) {
  const project = useStudio((s) => s.project);
  const user = useStudio((s) => s.user);
  const [t, setT] = useState<{ xIsA: boolean; gap: number; on: 'X' | 'Y' | null; rx: Partial<Ratings>; ry: Partial<Ratings>; shown: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => () => { engine.setCompare(null); }, []);

  async function start() {
    setBusy(true);
    try {
      const gap = await loudnessGap(project, baseline, win);
      setT({ xIsA: Math.random() < 0.5, gap, on: null, rx: {}, ry: {}, shown: false });
    } finally { setBusy(false); }
  }
  function listen(which: 'X' | 'Y') {
    if (!t) return;
    const isA = which === 'X' ? t.xIsA : !t.xIsA;
    engine.setCompare(isA ? null : matched(baseline, t.gap));
    setT({ ...t, on: which });
    if (!useStudio.getState().transport.playing) void engine.play(win.from, win.from + win.len);
  }
  const avg = (r: Partial<Ratings>) => { const v = CATS.map((c) => r[c.id]).filter((x): x is number => !!x); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : NaN; };
  const complete = t && CATS.every((c) => t.rx[c.id] && t.ry[c.id]);

  if (!t) return (
    <div className="card stack">
      <b>Blindvergleich</b>
      <div className="t2" style={{ fontSize: 13 }}>Mix mit Vorschlägen und Ausgangsstand in zufälliger Reihenfolge als X und Y, lautstärkegleich. Ihr bewertet Balance, Klarheit, Panorama, Raum und Dynamik von 1 bis 5, erst danach wird aufgelöst. Jede Person im Camp kann den Vergleich für sich machen.</div>
      <div><button className="btn" disabled={busy} onClick={() => void start()}>{busy ? 'Lautheit wird angeglichen …' : 'Blindvergleich starten'}</button></div>
    </div>
  );

  const name = (w: 'X' | 'Y') => ((w === 'X') === t.xIsA ? 'mit Auto-Mix-Vorschlägen' : 'Ausgangsstand ohne Auto-Mix');
  return (
    <div className="card stack">
      <div className="row" style={{ justifyContent: 'space-between' }}><b>Blindvergleich</b><span className="t2" style={{ fontSize: 12 }}>lautstärkegleich, Unterschied {n1(Math.abs(t.gap), 'dB')} ausgeglichen</span></div>
      <div className="row">
        {(['X', 'Y'] as const).map((w) => <button key={w} className={`btn ${t.on === w ? 'on' : ''}`} onClick={() => listen(w)}>{w} hören</button>)}
        <button className="btn" onClick={() => void togglePlay()}>Abspielen / Stopp</button>
      </div>
      <table className="amtable">
        <thead><tr><th>Kategorie</th><th>X</th><th>Y</th></tr></thead>
        <tbody>
          {CATS.map((c) => (
            <tr key={c.id}>
              <td>{c.label}<div className="t2" style={{ fontSize: 11 }}>{c.d}</div></td>
              {(['rx', 'ry'] as const).map((k) => (
                <td key={k}><div className="seg rate">{[1, 2, 3, 4, 5].map((v) => <button key={v} className={t[k][c.id] === v ? 'on' : ''} disabled={t.shown} onClick={() => setT({ ...t, [k]: { ...t[k], [c.id]: v } })}>{v}</button>)}</div></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {!t.shown ? (
        <div className="row"><button className="btn primary" disabled={!complete} onClick={() => {
          const a = t.xIsA ? avg(t.rx) : avg(t.ry), b = t.xIsA ? avg(t.ry) : avg(t.rx);
          setT({ ...t, shown: true });
          onDone(`${user.name.split(' ')[0]}, Blindvergleich: mit Auto-Mix ${n1(a)}, ohne ${n1(b)} (von 5)`);
        }}>Auflösen</button>{!complete && <span className="t2">Bitte alle Kategorien für X und Y bewerten.</span>}</div>
      ) : (
        <div className="stack">
          <div>X war <b>{name('X')}</b> (Ø {n1(avg(t.rx))}), Y war <b>{name('Y')}</b> (Ø {n1(avg(t.ry))}).</div>
          <div className="row"><button className="btn small" onClick={() => { engine.setCompare(null); setT(null); }}>Neuer Durchgang</button></div>
        </div>
      )}
    </div>
  );
}
